import { pick } from 'lodash';
import { JSX, useEffect } from 'react';
import { PageLoading } from '../../components/page';
import { useAccessValidation } from '../../contexts/accessValidationContext';
import { appendSearchToRoute, useCurrentRouteSearch } from '../../routes';
import { getAndClearRedirectUrl } from '../../utils/authenticationStore';
import { parseMrnIntoRoute } from '../../utils/mrn';
import { useSmartNavigate } from '../../utils/useSmartNavigate';

// A list of search params that should be preserved (passed through) during the redirect.
// These are preserved for marketing purposes.
const searchPassthrough = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

/**
 * Post-login page that handles access checks and redirects after successful authentication.
 * This page waits for access validation to complete before performing redirects.
 * If the user doesn't have access (e.g., needs to go to patient portal), the global
 * Apollo error handler will catch it and show the appropriate blocked page.
 */
export const PostLoginPage = (): JSX.Element => {
  const navigate = useSmartNavigate();
  const searchParams = useCurrentRouteSearch();

  // Use the shared access validation context to avoid duplicate queries
  const { canProceed, validateAccess } = useAccessValidation();

  useEffect(() => {
    // Trigger the access validation check
    // If the user is not onboarded or doesn't have access, this will throw an error
    // that gets handled by the global Apollo error handler
    validateAccess();
  }, [validateAccess]);

  useEffect(() => {
    // Only proceed with redirect after access validation completes successfully
    if (!canProceed) {
      return; // Still validating or has errors
    }

    const performRedirect = async (): Promise<void> => {
      const storedRedirectUrl = await getAndClearRedirectUrl();
      const mrn = searchParams.mrn ?? '';

      let { to: redirectUrl, toOptions } = parseMrnIntoRoute(mrn);

      if (mrn === '' && storedRedirectUrl != null && storedRedirectUrl.trim() !== '') {
        // If no MRN was provided, use any locally stored redirect route.
        redirectUrl = storedRedirectUrl;
      }

      // If no MRN and no stored redirect, default to home
      if (mrn === '' && storedRedirectUrl != null && storedRedirectUrl.trim() === '') {
        redirectUrl = '/home';
        toOptions = {};
      }

      // Gets a list of search parameters that should be preserved during the redirect.
      const toSearch = pick(searchParams, searchPassthrough);
      const to = appendSearchToRoute(redirectUrl, toSearch);
      navigate(to, { ...toOptions, replace: true });
    };

    void performRedirect();
  }, [navigate, searchParams, canProceed]);

  // Show loading while the access checks and redirect are happening
  return <PageLoading pageName="Post-Login Redirect" />;
};
